import { ArrowLeftOutlined } from '@ant-design/icons';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
    Card, Col, Flex, Row, theme, Typography,
} from 'antd';
import {
    useCallback, useEffect, useMemo, useState,
} from 'react';
import { Helmet } from 'react-helmet';
import {
    Link,
    useNavigate, useParams, useSearchParams,
} from 'react-router-dom';

import { projectsQueries, scriptsMutations } from '@/api';
import {
    ConfirmModal, CrossSellsModal, getActionControlStyleVars, ScriptForm, ScriptFormSubmitData,
} from '@/components';
import { AppRoute } from '@/constants';
import { useBackToOrList } from '@/hooks';
import { useUIStore } from '@/stores';

export const ScriptCreatePage = () => {
    const { projectId } = useParams<{ projectId: string }>();
    const navigate = useNavigate();
    const { token } = theme.useToken();
    const [searchParams] = useSearchParams();
    const scriptsListPath = projectId ? `/projects/${projectId}` : AppRoute.Scripts;
    const handleBack = useBackToOrList(scriptsListPath);
    const [confirmModalOpen, setConfirmModalOpen] = useState(false);
    const [crossSellsModalOpen, setCrossSellsModalOpen] = useState(false);
    const [createdScriptId] = useState<string | null>(null);
    const [formData, setFormData] = useState<ScriptFormSubmitData | null>(null);

    const defaultType = useMemo(() => {
        const typeParam = searchParams.get('type');
        return (typeParam === 'default' || typeParam === 'cross-sale') ? typeParam : 'default';
    }, [searchParams]);

    const { setPageTitle } = useUIStore();
    const { data: project } = useQuery({
        ...projectsQueries.getOneProject(projectId || ''),
        enabled: !!projectId,
    });

    useEffect(() => {
        setPageTitle(project?.title ?? null);
        return () => setPageTitle(null);
    }, [project?.title, setPageTitle]);

    const createMutationOptions = scriptsMutations.create(projectId || '');
    const { mutate: createScript, isPending } = useMutation(createMutationOptions);
    const { mutate: bulkAttach } = useMutation(scriptsMutations.bulkAttachCrossSell(projectId || ''));

    const handleSubmit = useCallback((data: ScriptFormSubmitData) => {
        setFormData(data);
        setConfirmModalOpen(true);
    }, []);

    const handleConfirm = useCallback(() => {
        if (formData && projectId) {
            createScript(formData, {
                onSuccess: (newScript) => {
                    if (formData.addToAllScripts && formData.type === 'cross-sale') {
                        bulkAttach({ cross_sell_ids: [newScript.id] });
                    }
                    setConfirmModalOpen(false);
                    setFormData(null);
                    navigate(projectId ? `/projects/${projectId}` : AppRoute.Scripts);
                },
            });
        }
    }, [formData, createScript, bulkAttach, projectId, navigate]);

    const handleCloseCrossSellsModal = useCallback(() => {
        setCrossSellsModalOpen(false);
        if (createdScriptId && projectId) {
            navigate(`/projects/${projectId}/scripts/${createdScriptId}`);
        }
    }, [createdScriptId, projectId, navigate]);

    const handleOpenCrossSellsModal = useCallback(() => {
        if (createdScriptId) {
            setCrossSellsModalOpen(true);
        }
    }, [createdScriptId]);

    const handleCrossSellsSuccess = useCallback(() => {
        setCrossSellsModalOpen(false);
        if (createdScriptId && projectId) {
            navigate(`/projects/${projectId}/scripts/${createdScriptId}`);
        }
    }, [createdScriptId, projectId, navigate]);

    const handleCancel = useCallback(() => {
        handleBack();
    }, [handleBack]);

    const handleModalCancel = useCallback(() => {
        setConfirmModalOpen(false);
        setFormData(null);
    }, []);

    return (
        <>
            <Helmet>
                <title>Створити скрипт</title>
            </Helmet>

            <Row justify="center">
                <Col xs={24} sm={24} md={20} lg={18} xl={16}>
                    <Card
                        title={
                            <Flex align="center" gap="middle">
                                <Link
                                    to={scriptsListPath}
                                    aria-label="Назад"
                                    className="app-action-control app-action-control--icon"
                                    style={getActionControlStyleVars(token, {
                                        variant: 'text',
                                        bordered: false,
                                        iconOnly: true,
                                    })}
                                >
                                    <ArrowLeftOutlined style={{ fontSize: 16 }} />
                                </Link>
                                <Typography.Title level={4} style={{ margin: 0 }}>
                                    Створити скрипт
                                </Typography.Title>
                            </Flex>
                        }
                    >
                        <ScriptForm
                            onSubmit={handleSubmit}
                            onCancel={handleCancel}
                            loading={isPending}
                            cancelButtonText="Відмінити"
                            cancelTo={scriptsListPath}
                            defaultType={defaultType}
                            projectId={projectId}
                            scriptId={createdScriptId || undefined}
                            onOpenCrossSellsModal={handleOpenCrossSellsModal}
                            isCreating={!createdScriptId}
                        />
                    </Card>
                </Col>
            </Row>

            <ConfirmModal
                open={confirmModalOpen}
                mode="create"
                entityType="script"
                entityName={formData?.title}
                onConfirm={handleConfirm}
                onCancel={handleModalCancel}
                loading={isPending}
            />

            {projectId && createdScriptId && (
                <CrossSellsModal
                    open={crossSellsModalOpen}
                    projectId={projectId}
                    scriptId={createdScriptId}
                    currentCrossSells={[]}
                    onClose={handleCloseCrossSellsModal}
                    onSuccess={handleCrossSellsSuccess}
                />
            )}
        </>
    );
};
